import { Button, Row, Tab, Tabs } from 'react-bootstrap';
import currencyOptions from '../Constant/currencyOptions';
import ApplicationGeneralTab from '../Approvable/components/ApprovableGeneralInformationTab';
import ApprovableSendBackModal from '../Approvable/components/ApprovableSendBackModal';
import applicationDetailSignal from './ApplicationDetail.signal';
import {
  fetchLoanTrackingOptions,
  hideApplicationDetailsModal,
} from './ApplicationDetail.helpers';
import { APPLICATION_LOAN_TRACKING_REFRESH_EVENT } from 'libs/loanProcessingBatchesDashboardDirty';
import ApplicationWatchlistTab from './components/ApplicationWatchlistTab';
import userAccountSignal from 'signals/UserAccount.signal';
import React, { useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import ApplicationApprovalChainTab from './components/ApplicationApprovalChainTab/ApplicationApprovalChainTab';
import loaderSignal from 'signals/Loader.signal';
import ApplicationBlockchainTab from './components/ApplicationBlockchainTab';
import { ocrSignal } from '../OCR/ocr.signal';
import ApprovableLinkBankAccountAlert from '../Approvable/components/ApprovableLinkBankAccountAlert';
import { showSendBackApplicationModal } from '../Approvable/components/ApprovableSendBackModal.helpers';
import { AmortizationScheduleTab, LedgerTab } from '../Ledger/Ledger';
import ApprovableStatusTab from '../Approvable/components/ApprovableStatusTab';
import ApprovableNotesTab from '../Approvable/components/ApprovableNotesTab';
import ApprovableDocumentsTab from '../Approvable/components/ApprovableDocumentsTab';
import ApplicationFormTab from './components/ApplicationFormTab';
import ApplicationEventsTab from './components/ApplicationEventsTab';
import ApplicationDetailsTab from './components/ApplicationDetailsTab';
import ApplicationDisbursementsTab from './components/ApplicationDisbursementsTab';
import ApplicationCspInfoTab from './components/ApplicationCspInfoTab';
import ApplicationClaimsTab from './components/ApplicationClaimsTab';
import $appSettings from 'signals/AppSettings.signal';
import { PROGRAM_KEYS } from '../Constant/constants';
import { LOAN_PROCESSING_BATCH_STATUS_LABELS } from 'libs/loanProcessingBatchStatusLabels';

type Props = {
  isModal: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
};

const ApplicationDetail: React.FC<Props> = ({ isModal, canUpdate = true, canDelete = true }) => {
  const { pathname, search } = useLocation();
  const history = useHistory();
  const userData = userAccountSignal.value.userData;
  const {
    edoUnread,
    lenderUnread,
    status,
    edoUnreadDate,
    ventureCapitalist,
    fundingProgram,
    approvedAmount,
    calcapLoanNumber,
    lenderLoanNumber,
    lender,
    modelType,
    processingBatchMemberships,
  } = applicationDetailSignal.value;

  const isPortfolioView = pathname.split('/')[2] === 'portfolio';
  const isCspOrCollateralSupport =
    fundingProgram?.referenceProgram?.key === PROGRAM_KEYS.collateralSupport ||
    fundingProgram?.name === 'CSP' ||
    fundingProgram?.name === 'Collateral Support';
  const showCspInfoTab = isPortfolioView && isCspOrCollateralSupport;
  const showClaimsTab = isPortfolioView;
  const { APPLICATION_STATUS, PORTAL_TYPES } = $appSettings.value.constants;
  const { isContentLoading } = loaderSignal.value;
  const activeTab = new URLSearchParams(search).get('tab');
  const isEdoPortal = userData.account.portalType === PORTAL_TYPES.edo;
  const tabForUi =
    !isEdoPortal && activeTab === 'loanTracking'
      ? 'general'
      : activeTab || 'general';
  const topRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => () => {
      ocrSignal.reset();
      applicationDetailSignal.reset();
    },
    []
  );

  useEffect(() => {
    if (activeTab) {
      const resolved =
        !isEdoPortal && activeTab === 'loanTracking' ? 'general' : activeTab;
      applicationDetailSignal.update({ currentTab: resolved });
    }
  }, [activeTab, isEdoPortal]);

  useEffect(() => {
    const onLoanTrackingLightRefresh = (e: Event) => {
      const { id, modelType, currentTab } = applicationDetailSignal.value;
      if (modelType !== 'application' || id == null) return;
      if (currentTab === 'loanTracking') return;
      const ce = e as CustomEvent<{ applicationId?: number; applicationIds?: number[] }>;
      const { applicationId: oneId, applicationIds } = ce.detail || {};
      if (applicationIds?.length) {
        if (!applicationIds.includes(id)) return;
      } else if (oneId != null && oneId !== id) {
        return;
      }
      void (async () => {
        const options = await fetchLoanTrackingOptions(id);
        const batchMemberships = Array.isArray(options.processingBatchMemberships)
          ? options.processingBatchMemberships
          : [];
        applicationDetailSignal.update({ processingBatchMemberships: batchMemberships });
      })();
    };
    window.addEventListener(APPLICATION_LOAN_TRACKING_REFRESH_EVENT, onLoanTrackingLightRefresh);
    return () =>
      window.removeEventListener(APPLICATION_LOAN_TRACKING_REFRESH_EVENT, onLoanTrackingLightRefresh);
  }, []);

  useEffect(() => {
    if (!isEdoPortal && activeTab === 'loanTracking') {
      const params = new URLSearchParams(search);
      params.delete('tab');
      const nextSearch = params.toString();
      history.replace({ pathname, search: nextSearch ? `?${nextSearch}` : '' });
    }
  }, [isEdoPortal, activeTab, pathname, search, history]);

  const loanInfoTotalAmount =
    approvedAmount != null && !Number.isNaN(Number(approvedAmount))
      ? Number(approvedAmount).toLocaleString(
          'en-US',
          currencyOptions.currencyUS as Intl.NumberFormatOptions
        )
      : '—';

  const latestBatchMembership =
    isEdoPortal &&
    Array.isArray(processingBatchMemberships) &&
    processingBatchMemberships.length > 0
      ? processingBatchMemberships[0]
      : null;
  const latestBatchStatusLabel =
    latestBatchMembership?.batch.status != null
      ? LOAN_PROCESSING_BATCH_STATUS_LABELS[latestBatchMembership.batch.status] ??
        latestBatchMembership.batch.status
      : null;

  const isCapitalAccessProgram =
    fundingProgram?.referenceProgram?.key === PROGRAM_KEYS.capitalAccess;
  const lenderCalcapIdHeader =
    lender?.otherOrganizationId != null && String(lender.otherOrganizationId).trim() !== ''
      ? String(lender.otherOrganizationId).trim()
      : '—';

  return (
    <div ref={topRef}>
      <ApprovableLinkBankAccountAlert />
      {modelType === 'application' && (
        <div className="mb-3 px-0 pl-3 d-flex flex-wrap align-items-baseline">
          <span className="font-weight-bold text-secondary text-dark">Loan Information</span>
          <span className="ml-3 text-secondary">
            <span className="font-weight-bold">Total Amount</span> {loanInfoTotalAmount}
          </span>
          <span className="ml-3 text-secondary">
            <span className="font-weight-bold">CalCAP Loan #</span> {calcapLoanNumber ?? '—'}
          </span>
          <span className="ml-3 text-secondary">
            <span className="font-weight-bold">Lender Loan Number</span>{' '}
            {lenderLoanNumber && String(lenderLoanNumber).trim() !== ''
              ? String(lenderLoanNumber).trim()
              : '—'}
          </span>
          {isCapitalAccessProgram && (
            <span className="ml-3 text-secondary">
              <span className="font-weight-bold">Lender CalCAP ID</span> {lenderCalcapIdHeader}
            </span>
          )}
          {latestBatchMembership != null && (
            <span className="text-secondary ml-auto pl-3 text-right">
              <span className="font-weight-bold text-secondary text-dark">Batch</span>{' '}
              {latestBatchMembership.batch.name || '—'} - {latestBatchStatusLabel ?? '—'}
            </span>
          )}
        </div>
      )}
      <Tabs className="px-0"
        defaultActiveKey={tabForUi}
        onSelect={(e) =>
          applicationDetailSignal.update({ currentTab: e as string })
        }
      >
        <Tab eventKey="general" title="General">
          <div
            className={
              isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
            }
          >
            <ApplicationGeneralTab isModal={isModal} />
          </div>
        </Tab>
        {isEdoPortal && (
          <Tab eventKey="loanTracking" title="Loan Tracking" mountOnEnter>
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <ApplicationEventsTab />
            </div>
          </Tab>
        )}
        <Tab eventKey="status" title="Status" mountOnEnter>
          <div
            className={
              isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
            }
          >
            <ApprovableStatusTab />
          </div>
        </Tab>
        <Tab eventKey="notes" title="Notes" mountOnEnter>
          <div
            className={
              isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
            }
          >
            <ApprovableNotesTab />
          </div>
        </Tab>
        <Tab eventKey="applicationDetails" title="Application Details" mountOnEnter>
          <div
            className={
              isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
            }
          >
            <ApplicationDetailsTab />
          </div>
        </Tab>
        <Tab eventKey="documents" title="Documents" mountOnEnter>
          <div
            className={
              isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
            }
          >
            <ApprovableDocumentsTab />
          </div>
        </Tab>
        <Tab eventKey="ocrInformation" title="OCR Document" mountOnEnter>
          <div
            className={
              isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
            }
          >
            <ApplicationFormTab />
          </div>
        </Tab>
        {showClaimsTab && (
          <Tab eventKey="claims" title="Claims" mountOnEnter>
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <ApplicationClaimsTab />
            </div>
          </Tab>
        )}
        {showCspInfoTab && (
          <Tab eventKey="cspInfo" title="Collateral Support" mountOnEnter>
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <ApplicationCspInfoTab />
            </div>
          </Tab>
        )}
        {userAccountSignal.value.userData.account.portalType ===
          PORTAL_TYPES.edo && (
          <Tab eventKey="blockchain" title="Blockchain" mountOnEnter unmountOnExit>
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <ApplicationBlockchainTab />
            </div>
          </Tab>
        )}
        {applicationDetailSignal.value.status ===
          APPLICATION_STATUS.approved && (
          <Tab eventKey="amortizationSchedule" title="Amortization Schedule">
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <AmortizationScheduleTab />
            </div>
          </Tab>
        )}
        {applicationDetailSignal.value.status ===
          APPLICATION_STATUS.approved && (
          <Tab eventKey="ledger" title="Ledger" mountOnEnter>
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <LedgerTab isModal={isModal} />
            </div>
          </Tab>
        )}
        {userAccountSignal.value.userData.account.portalType ===
          PORTAL_TYPES.lender && (
          <Tab eventKey="watchlist" title="Watchlist">
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <ApplicationWatchlistTab />
            </div>
          </Tab>
        )}
        {userAccountSignal.value.userData.account.portalType ===
          PORTAL_TYPES.edo && (
          <Tab
            eventKey="approvalChain"
            title="Approval Chain"
            mountOnEnter
          >
            <div
              className={
                isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
              }
            >
              <ApplicationApprovalChainTab />
            </div>
          </Tab>
        )}
        {userAccountSignal.value.userData.account.portalType ===
          PORTAL_TYPES.edo && (
            <Tab eventKey="disbursements" title="Disbursements" mountOnEnter>
              <div
                className={
                  isModal ? 'p-32' : 'bg-white border border-gray p-32 shadow-sm'
                }
              >
                <ApplicationDisbursementsTab />
              </div>
            </Tab>
          )}
      </Tabs>
      <Row
        className="mt-16 px-16 justify-content-between align-items-center"
      >
        <div>
          <Button
            variant="outline-secondary"
            className="rounded"
            onClick={() =>
              topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            Back to Top
          </Button>
        </div>
        <div>
          <Button
            hidden={pathname.split('/').length > 3}
            variant="outline-secondary"
            className="rounded mr-8"
            onClick={hideApplicationDetailsModal}
          >
            Close
          </Button>
          {canUpdate && (
            <Button
              className="rounded"
              title={
                edoUnread || lenderUnread
                  ? 'Please wait for the application to be sent back.'
                  : ''
              }
              disabled={
                isContentLoading ||
                (userData.account.portalType === PORTAL_TYPES.edo
                  ? lenderUnread
                  : edoUnread)
              }
              hidden={
                userData.account.portalType === PORTAL_TYPES.edo
                  ? status !== APPLICATION_STATUS.incomplete &&
                    status !== APPLICATION_STATUS.pending
                  : status !== APPLICATION_STATUS.incomplete
              }
              onClick={() => showSendBackApplicationModal()}
            >
            {userData.account.portalType === PORTAL_TYPES.edo
              ? status !== APPLICATION_STATUS.incomplete
                ? `Send back to ${ventureCapitalist ? 'VC' : 'Lender'}`
                : `Send back to ${ventureCapitalist ? 'VC' : 'Lender'} ${
                    edoUnreadDate
                      ? new Date(edoUnreadDate).toLocaleDateString()
                      : ''
                  }`
              : 'Send back to EDO'}
            </Button>
          )}
        </div>
      </Row>
      <ApprovableSendBackModal />
    </div>
  );
};

export default ApplicationDetail;
